import { useTranslation } from "react-i18next";

import { Notification } from "./notification";
import { useToastCampaign } from "./use-toast-campaign";

/**
 * The two things a jackpot toast can say. `won` is the reader's own win (from their
 * `jackpot_won` notification): the gold card, "You won · €618.20 · Progg". `announce` is
 * somebody else's, seen by everyone watching the shared progress stream: the plain card,
 * "ale** just won €618.20" over the jackpot's name. Amount and title arrive server-formatted
 * and translated, so they go out verbatim.
 */
export type JackpotToastCopy =
  | { kind: "won"; amount: string; jackpotTitle: string }
  | { kind: "announce"; name: string; amount: string; jackpotTitle: string };

/**
 * A jackpot event as a toast. Tapping it opens the campaign (`useToastCampaign`).
 */
export const JackpotToast = ({
  campaignId,
  copy,
  onDismiss,
}: {
  campaignId: number;
  copy: JackpotToastCopy;
  onDismiss: () => void;
}) => {
  const { t } = useTranslation();
  const { imageUrl, onClick } = useToastCampaign(campaignId, onDismiss);

  if (copy.kind === "won") {
    return (
      <Notification
        // `prizes.youWon` ships translated in every locale; a reward reads the same everywhere.
        label={t("prizes.youWon")}
        title={`${copy.amount} · ${copy.jackpotTitle}`}
        imageUrl={imageUrl}
        variant="prize"
        onClick={onClick}
      />
    );
  }

  return (
    <Notification
      // The same sentence the card shows under the pot in its just-won state.
      title={t("jackpot.justWonBy", "{{name}} just won {{amount}}", {
        name: copy.name,
        amount: copy.amount,
      })}
      text={copy.jackpotTitle}
      imageUrl={imageUrl}
      variant="default"
      onClick={onClick}
    />
  );
};
